import React, { useState } from "react";
import request from "request";
import Card from "Card";
import Table from "Table";
import Button from "Button";
import Icon from "Icon";
import Paginate from "Paginate";
import moment from "moment";
import User from "User";
import barcodeUrl from "./barcodeUrl";

const ResetCreditsTab = ({ prepaidCredits, pagy, fetchCredits, dataSource = "internal", headers}) => {
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetStatus, setResetStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [searchCode, setSearchCode] = useState("");
  const [initialBalanceMin, setInitialBalanceMin] = useState("");
  const [initialBalanceMax, setInitialBalanceMax] = useState("");
  const [currentBalanceMin, setCurrentBalanceMin] = useState("");
  const [currentBalanceMax, setCurrentBalanceMax] = useState("");
  const [downloading, setDownloading] = useState(false);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchCreditsWithFilters(newPage);
  };

  const fetchCreditsWithFilters = (pageNum = 1) => {
    const params = new URLSearchParams({
      page: pageNum,
      per_page: perPage,
    });

    if (searchCode) params.append('code', searchCode);
    if (initialBalanceMin) params.append('initial_balance_min', initialBalanceMin);
    if (initialBalanceMax) params.append('initial_balance_max', initialBalanceMax);
    if (currentBalanceMin) params.append('current_balance_min', currentBalanceMin);
    if (currentBalanceMax) params.append('current_balance_max', currentBalanceMax);

    fetchCredits(pageNum, params.toString());
  };

  const handleSearch = () => {
    setPage(1);
    fetchCreditsWithFilters(1);
  };

  const handleDownloadCSV = () => {
    setDownloading(true);

    const params = new URLSearchParams();
    if (searchCode) params.append('code', searchCode);
    if (initialBalanceMin) params.append('initial_balance_min', initialBalanceMin);
    if (initialBalanceMax) params.append('initial_balance_max', initialBalanceMax);
    if (currentBalanceMin) params.append('current_balance_min', currentBalanceMin);
    if (currentBalanceMax) params.append('current_balance_max', currentBalanceMax);

    const url = `/api/${dataSource}/prepaid_credits/download?${params.toString()}`;

    const authToken = User.getAuthToken();
    const csrfToken = document.getElementsByName('csrf-token')[0].content;

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', authToken);
    xhr.setRequestHeader('X-CSRF-Token', csrfToken);
    xhr.setRequestHeader('X-CURRENT-PAGE', window.location.href);
    xhr.responseType = 'blob';

    xhr.onload = () => {
      setDownloading(false);
      if (xhr.status === 200) {
        const blob = xhr.response;
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `Prepaid Credits - ${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        console.error('Download failed with status:', xhr.status);
        alert('Download failed. Please try again.');
      }
    };

    xhr.onerror = () => {
      setDownloading(false);
      console.error('XHR error occurred');
      alert('Download failed. Please try again.');
    };

    xhr.send();
  };

  const handleResetCredits = () => {
    if (!resetConfirm) {
      setResetConfirm(true);
      return;
    }

    let req = request.post("internal/prepaid_credits/reset");
    req.then(({data}) => {
      if (data.success) {
        setResetStatus("All credits have been reset successfully.");
        fetchCredits();
      }
    }).catch(error => {
      alert(`Error resetting credits: ${error.message}`);
    });

    // Reset confirmation after 3 seconds
    setTimeout(() => {
      setResetConfirm(false);
      setResetStatus(null);
    }, 3000);
  };

  return (
    <div className="column is-12">
      {dataSource === "internal" && (
        <Card title="Reset Credits">
          <p className="mb-4">
            This action will reset all credits to their initial balance and mark them as active. Use with caution.
          </p>

          {resetStatus && (
            <div className="notification is-success mb-4">
              <Icon icon="check" className="mr-1" /> {resetStatus}
            </div>
          )}

          <Button
            className={resetConfirm ? "is-danger" : "is-warning"}
            onClick={handleResetCredits}
            icon="sync"
            text={resetConfirm ? "Confirm Reset All Credits" : "Reset All Credits"}
          />

          {resetConfirm && (
            <p className="has-text-danger mt-3 is-flex is-align-items-center">
              <Icon icon="exclamation-triangle" className="mr-2" />
              <span>This action cannot be undone!</span>
            </p>
          )}
        </Card>
      )}

      <Card title="Current Credits">
        <div className="mb-4">
          <div className="columns">
            <div className="column is-4">
              <label className="label">Search by Code</label>
              <div className="field has-addons">
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="text"
                    placeholder="Enter code..."
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="control">
                  <Button
                    className="is-info"
                    onClick={handleSearch}
                    text="Search"
                  />
                </div>
              </div>
            </div>
            <div className="column is-8">
              <Button
                className="is-primary is-pulled-right"
                onClick={handleDownloadCSV}
                disabled={downloading}
                text={downloading ? "Downloading..." : "Download CSV"}
                icon="download"
              />
            </div>
          </div>

          <div className="columns">
            <div className="column is-3">
              <label className="label">Initial Balance Min</label>
              <input
                className="input"
                type="number"
                placeholder="Min"
                value={initialBalanceMin}
                onChange={(e) => setInitialBalanceMin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="column is-3">
              <label className="label">Initial Balance Max</label>
              <input
                className="input"
                type="number"
                placeholder="Max"
                value={initialBalanceMax}
                onChange={(e) => setInitialBalanceMax(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="column is-3">
              <label className="label">Current Balance Min</label>
              <input
                className="input"
                type="number"
                placeholder="Min"
                value={currentBalanceMin}
                onChange={(e) => setCurrentBalanceMin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="column is-3">
              <label className="label">Current Balance Max</label>
              <input
                className="input"
                type="number"
                placeholder="Max"
                value={currentBalanceMax}
                onChange={(e) => setCurrentBalanceMax(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          </div>
        </div>

        <Table
          headers={headers}
          showHeader={true}
          formatters={{
            code: (c) => (
              <a
                href={barcodeUrl(c.code)}
                target="_blank"
                rel="noopener noreferrer"
                title="Open barcode in a new tab"
                onClick={(e) => e.stopPropagation()}
              >
                {c.code}
              </a>
            ),
            venue: (c) => c.venue.name,
            status: (c) => (
              <span className={`tag ${!c.active ? "is-danger" : "is-success"}`}>
                {!c.active ? "Inactive" : "Active"}
              </span>
            ),
            createdAt: (c) => moment(c.createdAt).format("MM/DD/YYYY")
          }}
          rows={prepaidCredits || []}
        />

        {pagy && (
          <Paginate
            changePage={handlePageChange}
            {...pagy}
          />
        )}
      </Card>
    </div>
  );
};

export default ResetCreditsTab;
